/* The exercise table — KANON `Cwiczenia` 17.09.2026, variant e.
 *
 * A CSS grid carrying ARIA table roles, for the reason `client-roster`'s table is one: the column
 * labels are what tell PARTIE from SPRZĘT for anyone not looking at the screen, and a bare grid of
 * `<div>`s cannot associate a label with the cell under it. A real `<table>` cannot hold these
 * tracks — under `table-layout: fixed` a `<col>` width written as `minmax()` is read as `auto` and
 * the browser splits the flexible columns equally.
 *
 * Every text leaf STATES its line-height. `base.css` puts `--lh-relaxed` on `body`, and an unstated
 * leaf renders about 2px taller than the specimen, which drifts the whole 52px row. */

/* The scroller. The keyboard's way in, so it is a named region with its own tab stop. */
.scroller {
  --sb-size: 10px;
  --sb-radius: var(--radius-xs);

  flex: 1;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
}

.scroller:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The grid's own floor. Below it the table scrolls sideways INSIDE the card rather than letting the
 * columns collapse — a clip with no scroll path is the one outcome `CLAUDE.md` calls worse than
 * either. 260 + 116 + 200 + 100 tracks, three 20px gaps, 22px of inset each side = 780. */
.table {
  display: flex;
  flex-direction: column;
  min-width: 780px;
}

/* Four tracks: Ćwiczenie, Typ, Partie, Sprzęt — one shape in every state. The Autor column went
 * with the owner's polish of 4 November 2026, and so did a Tagi column (owner, same day): whose
 * movement it is and whether it needs a look read as chips after the name, and the trainer's tags
 * live in the bar above the table and in the card. The freed width went to the name and muscles. */
.gridRow {
  display: grid;
  grid-template-columns:
    minmax(260px, 2fr) 116px minmax(200px, 1.3fr) 100px;
  gap: var(--space-0) var(--space-20px);
  align-items: center;
  padding: var(--space-0) var(--space-22px);
}

/* Without the preview panel the table takes the width, and every track widens rather than letting
 * the flexible two absorb all of it — the muscle column then fits a THIRD pill, which is the point
 * of hiding the panel. Floor: 300 + 116 + 280 + 120 + three 24px gaps + 44 = 932. */
.wide .table {
  min-width: 932px;
}

.wide .gridRow {
  grid-template-columns:
    minmax(300px, 2fr) 116px minmax(280px, 1.4fr) 120px;
  column-gap: var(--space-6);
}

.headGroup {
  position: sticky;
  top: var(--space-0);
  z-index: 1;
}

/* The head sits on the inset surface and the rows on the card's, so a 3px lip of card colour above
 * it hides the sliver of row that shows through while the scroller is between two positions. The
 * specimen draws the same `::before` for the same reason. */
.headRow {
  position: relative;
  flex: none;
  height: 38px;
  background: var(--bg-inset);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headRow::before {
  position: absolute;
  top: -3px;
  right: var(--space-0);
  left: var(--space-0);
  height: 3px;
  content: '';
  background: var(--bg-card);
}

/* Sentence case at the label step, the words in `--text-2` and the glyphs a step quieter. */
.th {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  align-self: stretch;
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  text-align: left;
  white-space: nowrap;
  color: var(--text-2);
}

/* The sort control's hit area is the whole head row's height, not the 13px text box. */
.thButton {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  max-width: 100%;
  height: 100%;
  padding: var(--space-0);
  font: inherit;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.thButton:hover {
  color: var(--text);
}

.thButton:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The column the table is ordered by. Ink is one channel; the caret beside it is the second, and the
 * head's `aria-sort` is the third. */
.thActive {
  color: var(--text);
}

.thIcon {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--text-3);
}

.thCaret {
  flex: none;
  width: 9px;
  height: 9px;
}

/* Drawn pointing DOWN; ascending turns it over. One drawing, one transform. */
.thCaretUp {
  transform: rotate(180deg);
}

/* The rows' own box.
 *
 * It stands at the height of the WHOLE list whether or not a row is mounted — the table is virtual
 * (see `ExerciseTable`'s header), so the scrollbar and the scroll position have to go on describing
 * 479 rows while the DOM holds a few dozen. The height is the virtualizer's `getTotalSize()` and is
 * written inline, because it is data rather than a decision. */
.rows {
  position: relative;
  flex: none;
  width: 100%;
}

/* Every row is placed by the virtualizer, with a transform rather than a `top`: thirty rows are
 * re-placed on every scroll frame, and that is the compositor's work rather than the layout
 * engine's. The transform also makes the row the containing block its selection bar was already
 * positioned against. */
.row {
  position: absolute;
  top: var(--space-0);
  left: var(--space-0);
  width: 100%;
  height: var(--h-ex-table-row);
  border-bottom: var(--border-hairline) solid var(--border);

  /* The whole row opens the card — see `ExerciseTable`'s own header for why the handler is here and
   * the focusable control is the name inside it. */
  cursor: pointer;
}

.row:hover {
  background: var(--hover-tint);
}

/* The selection: the tint plus a 2px INK bar. Never the accent — on this screen ember marks the
 * header's `+` and nothing else. An absolutely positioned bar rather than an inset `box-shadow`,
 * because stylelint accepts only a token there and an ink bar is geometry rather than elevation. */
.rowSelected {
  background: var(--hover-tint);
}

.selectionBar {
  position: absolute;
  top: var(--space-0);
  left: var(--space-0);
  width: 2px;
  height: 100%;
  background: var(--text);
}

.td {
  min-width: 0;
  overflow: hidden;
  font-weight: var(--fw-regular);
  text-align: left;
}

/* ── The name cell ───────────────────────────────────────────────────────────────────────── */

.nameCell {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-width: 0;
}

/* 44 x 33 — the canon's own still. GEOMETRY only — the frame's treatment is `exerciseStill.module.css`'s, and nothing is declared on
 * both sides. See that file's header. */
.still {
  width: 44px;
  height: 33px;
  border-radius: var(--radius-xs);
}

/* The row's OPENER as well as its name — a real `<button>`, so an arrow lands on something with a
 * role and a name rather than on a `role="row"` that silently answers Enter. It is drawn as plain
 * text: the whole row is the target, the hover tint and the selection bar already say so, and a
 * button treatment on four hundred rows would be four hundred controls shouting at once. */
.name {
  min-width: 0;
  padding: var(--space-0);
  overflow: hidden;
  font-family: var(--font-body);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-name);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
  cursor: pointer;
  background: none;
  border: none;
}

/* „Twoje" and „Do uzupełnienia" after the name: `shared/ui/neutralChip` at its small cut. The pair
 * never shrinks — the name before it ellipsises instead. */
.statuses {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
}

/* „Do uzupełnienia" asks for a look, so it takes the stronger ink. Under `.statuses` so it outranks
 * the chip's own `color` whatever order the two sheets load in. */
.statuses .statusReview {
  color: var(--text);
}

/* The ring is drawn around the whole LINE rather than around the words: the arrows move between
 * rows and the row is what the trainer is on. `:has()` puts it on the row while keeping focus on
 * the control that actually has it, so the ring appears exactly where focus is.
 *
 * INSIDE the row — an outset ring on a 52px row whose neighbour is one hairline away would draw
 * over the row above it. `--outline-inset` is the app's own inward offset. */
.name:focus-visible {
  outline: none;
}

.row:has(.name:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* ── The type cell ───────────────────────────────────────────────────────────────────────── */

/* The type's glyph and its word, both in the type's own hue — the ONLY colour in a row: the pills,
 * the thumbs and the statuses are all neutral. No wash behind it. */
.typeMark {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  max-width: 100%;
  color: var(--type-ink);
}

.typeGlyph {
  display: block;
  flex: none;
  width: 16px;
  height: 16px;
}

.typeWord {
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── The muscles cell ────────────────────────────────────────────────────────────────────── */

.pills {
  display: flex;
  gap: var(--space-5px);
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.pill {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 22px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border-radius: var(--radius-8px);
}

/* A secondary muscle is the same fact in a weaker ink, not a different one. */
.pillSecondary {
  color: var(--text-2);
}


.more {
  flex: none;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* ── The equipment cell ──────────────────────────────────────────────────────────────────── */

.thumbs {
  display: flex;
  gap: var(--space-1);
  align-items: center;
}

/* 30px squares, side by side — never overlapping, so every piece is seen whole. */
.thumb {
  flex: none;
  width: 30px;
  height: 30px;
  overflow: hidden;
  background: var(--bg-card);
  border-radius: var(--radius-xs);
}

.thumbPhoto {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-xs);
}

/* A piece the catalogue names no photograph for. Its own initial, on the same disc, so a movement
 * with a picture and one without stand on the same line and nothing moves when one 404s. */
.thumbLetter {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  font-size: var(--fs-goal-lab);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-2);
  background: var(--bg-inset);
}

.thumbsMore {
  margin-left: var(--space-2px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}
